"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Slider from "react-slick";
import { AddToCartService, MainCart } from "@/app/services/cart";
import Image from "next/image";
import { useCurrencyCode } from "../helper-functions/CurrencySymbol";
import "./index.css";
import { useRouter } from "next/navigation";
import { Loader } from "../MainLoader/Loader";
import { BookingProgressBar } from "../../appoinments/BookingProgressBar";

const AllCartsSlide = () => {
  const addToCartService = useMemo(() => new AddToCartService(), []);
  const [allCarts, setAllCarts] = useState<MainCart | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const currencyCode = useCurrencyCode();
  const router = useRouter();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const settings = useMemo(
    () => ({
      dots: isSmallScreen ? true : false,
      infinite: true,
      autoplay: true,
      autoplaySpeed: 10000,
      arrows: isSmallScreen ? false : true,
      speed: 2000,
      slidesToShow: 1,
      slidesToScroll: 1,
      className: isSmallScreen
        ? "myCustomCarouselCartMobileView"
        : "myCustomCarouselCart",
    }),
    [isSmallScreen]
  );

  const getAllCarts = useCallback(() => {
    const mainCart = addToCartService.getCart();
    if (!mainCart) {
      // console.log("No cart found");
      return;
    }
    setAllCarts(mainCart);
  }, [addToCartService]);

  useEffect(() => {
    getAllCarts();
  }, [getAllCarts]);

  useEffect(() => {
    const intr = setInterval(() => {
      getAllCarts();
    }, 1000);
    return () => {
      clearInterval(intr);
    };
  }, [getAllCarts]);

  const NavigateViewCart = (item: any) => {
    setLoading(true);
    router.push("/cart");
    localStorage.setItem("cid", btoa(JSON.stringify(item?.cid)));
  };

  return (
    <>
      {loading && <Loader />}
      {allCarts && allCarts?.cartItemRequestsById?.length > 0 ? (
        allCarts?.cartItemRequestsById?.length == 1 ? (
          <Box
            sx={{
              position: "fixed",
              bottom: { xs: 85, sm: 20 },
              left: { xs: "50%", sm: "auto" },
              right: { xs: "auto", sm: 140 },
              transform: { xs: "translateX(-50%)", sm: "none" },
              background: "white",
              zIndex: 1000,
              borderRadius: "0.5rem",
              width: { xs: "95%", sm: "26rem" },
              height: "auto",
              boxShadow: "1px 1px 4px 1px rgba(0, 0, 0, 0.2)",
              cursor: "pointer",
            }}
          >
            {allCarts?.cartItemRequestsById?.map((item: any) => (
              <Box
                key={item.cid}
                sx={{
                  display: "flex !important",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: isSmallScreen ? "2px 8px" : "6px 32px",
                  flexDirection: "row",
                }}
              >
                <Box sx={{ marginRight: "0.5rem" }}>
                  <Image
                    height={200}
                    width={200}
                    alt="banner"
                    src={item?.cartItemRequests[0]?.ImageUrl}
                    style={{
                      height: isSmallScreen ? "50px" : "55px",
                      width: isSmallScreen ? "50px" : "55px",
                      minWidth: isSmallScreen ? "50px" : "55px",
                      minHeight: isSmallScreen ? "50px" : "55px",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      height: "40px",
                      alignContent: "center",
                    }}
                    variant="caption"
                  >
                    {item?.cartItemRequests
                      ?.map((service: any) => service.serviceName)
                      .join(", ")}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      {currencyCode}
                      {item?.totalAmount}
                    </Typography>
                    &nbsp; &nbsp;
                    <Typography
                      sx={{
                        textDecoration: "line-through",
                        color: "gray",
                      }}
                      variant="caption"
                    >
                      {currencyCode}
                      {item?.totalMrp}
                    </Typography>
                    &nbsp; &nbsp;
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      Save {currencyCode}
                      {item?.totalDiscount}
                    </Typography>
                  </Box>
                </Box>
                &nbsp; &nbsp; &nbsp;
                <Box>
                  <Button
                    sx={{
                      textTransform: "capitalize",
                      textWrap: "nowrap",
                      fontSize: "12px",
                      padding: "3px 8px",
                    }}
                    variant="outlined"
                    color="success"
                    onClick={() => NavigateViewCart(item)}
                  >
                    View Cart
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              position: "fixed",
              bottom: { xs: 85, sm: 20 },
              left: { xs: "50%", sm: "auto" },
              right: { xs: "auto", sm: 140 },
              transform: { xs: "translateX(-50%)", sm: "none" },
              background: "white",
              zIndex: 1000,
              borderRadius: "0.5rem",
              width: { xs: "95%", sm: "26rem" },
              height: "auto",
              boxShadow: "1px 1px 4px 1px rgba(0, 0, 0, 0.2)",
              cursor: "pointer",
            }}
          >
            <Slider {...settings}>
              {allCarts?.cartItemRequestsById?.map((item: any) => (
                <Box
                  key={item.cid}
                  sx={{
                    display: "flex !important",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: isSmallScreen ? "2px 8px" : "6px 32px",
                    flexDirection: "row",
                  }}
                >
                  <Box sx={{ marginRight: "0.5rem" }}>
                    <Image
                      height={200}
                      width={200}
                      alt="banner"
                      src={item?.cartItemRequests[0]?.ImageUrl}
                      style={{
                        height: isSmallScreen ? "50px" : "55px",
                        width: isSmallScreen ? "50px" : "55px",
                        minWidth: isSmallScreen ? "50px" : "55px",
                        minHeight: isSmallScreen ? "50px" : "55px",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        height: "40px",
                        alignContent: "center",
                      }}
                      variant="caption"
                    >
                      {item?.cartItemRequests
                        ?.map((service: any) => service.serviceName)
                        .join(", ")}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                        }}
                      >
                        {currencyCode}
                        {item?.totalAmount}
                      </Typography>
                      &nbsp; &nbsp;
                      <Typography
                        sx={{
                          textDecoration: "line-through",
                          color: "gray",
                        }}
                        variant="caption"
                      >
                        {currencyCode}
                        {item?.totalMrp}
                      </Typography>
                      &nbsp; &nbsp;
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                        }}
                      >
                        Save {currencyCode}
                        {item?.totalDiscount}
                      </Typography>
                    </Box>
                  </Box>
                  &nbsp; &nbsp; &nbsp;
                  <Box>
                    <Button
                      sx={{
                        textTransform: "capitalize",
                        textWrap: "nowrap",
                        fontSize: "12px",
                        padding: "3px 8px",
                      }}
                      variant="outlined"
                      color="success"
                      onClick={() => NavigateViewCart(item)}
                    >
                      View Cart
                    </Button>
                  </Box>
                </Box>
              ))}
            </Slider>
          </Box>
        )
      ) : (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 85, sm: 20 },
            left: { xs: "50%", sm: "auto" },
            right: { xs: "auto", sm: 140 },
            transform: { xs: "translateX(-50%)", sm: "none" },
            background: "white",
            zIndex: 1000,
            borderRadius: "0.5rem",
            width: { xs: "95%", sm: "26rem" },
            height: "auto",
            boxShadow: "1px 1px 4px 1px rgba(0, 0, 0, 0.2)",
            cursor: "pointer",
          }}
        >
          <BookingProgressBar />
        </Box>
      )}
    </>
  );
};

export default AllCartsSlide;
